import React, { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import ConfigPageLayout from '../components/ConfigPageLayout'

type Agent = {
  id: number
  name: string
  subtitle?: string | null
}

type Suggestion = {
  label: string
  question: string
}

type LandingDemoAgent = {
  agent_id: number
  label: string
  agent_name: string
  subtitle?: string | null
  welcome_title?: string | null
  quick_questions: string[]
  use_agent_avatar: boolean
  avatar_url?: string | null
}

type WidgetConfig = {
  public_widget_enabled: boolean
  internal_widget_enabled: boolean
  internal_agent_id: number | null
  allow_internal_agent_for_all_users: boolean
  internal_widget_suggestions: Suggestion[]
}

export default function WidgetConfigPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState<WidgetConfig>({
    public_widget_enabled: true,
    internal_widget_enabled: true,
    internal_agent_id: null,
    allow_internal_agent_for_all_users: true,
    internal_widget_suggestions: [],
  })
  const [agents, setAgents] = useState<Agent[]>([])
  const [agentsLoading, setAgentsLoading] = useState(true)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [landingDemoAgents, setLandingDemoAgents] = useState<LandingDemoAgent[]>([])
  const [landingDemoLoading, setLandingDemoLoading] = useState(true)

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/global-config')
      setConfig({
        public_widget_enabled: data.public_widget_enabled ?? true,
        internal_widget_enabled: data.internal_widget_enabled ?? true,
        internal_agent_id: data.internal_agent_id ?? null,
        allow_internal_agent_for_all_users: data.allow_internal_agent_for_all_users ?? true,
        internal_widget_suggestions: data.internal_widget_suggestions ?? [],
      })
    } catch (err: any) {
      setError(err?.message || 'Erreur lors du chargement de la configuration')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  // Fetch all agents (Super Admin sees all)
  useEffect(() => {
    const fetchAgents = async () => {
      setAgentsLoading(true)
      try {
        const { data } = await api.get('/agents')
        setAgents(data?.items || [])
      } catch {
        setAgents([])
      } finally {
        setAgentsLoading(false)
      }
    }
    fetchAgents()
  }, [])

  useEffect(() => {
    const fetchLandingDemoAgents = async () => {
      setLandingDemoLoading(true)
      try {
        const { data } = await api.get('/global-config/landing-demo-agents')
        const rows = Array.isArray(data?.items) ? data.items : []
        setLandingDemoAgents(
          rows.map((row: LandingDemoAgent) => ({
            ...row,
            quick_questions: row.quick_questions ?? [],
            use_agent_avatar: row.use_agent_avatar !== false,
          }))
        )
      } catch {
        setLandingDemoAgents([])
      } finally {
        setLandingDemoLoading(false)
      }
    }
    fetchLandingDemoAgents()
  }, [])

  const handleToggle = useCallback((field: keyof WidgetConfig) => {
    setConfig((prev) => ({ ...prev, [field]: !prev[field] }))
    setStatus(null)
    setError(null)
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    setStatus(null)
    setError(null)
    try {
      await api.put('/global-config', {
        public_widget_enabled: config.public_widget_enabled,
        internal_widget_enabled: config.internal_widget_enabled,
        internal_agent_id: config.internal_agent_id,
        allow_internal_agent_for_all_users: config.allow_internal_agent_for_all_users,
        internal_widget_suggestions: config.internal_widget_suggestions,
      })
      const { data: landingSaved } = await api.put('/global-config/landing-demo-agents', {
        items: landingDemoAgents.map((item) => ({
          agent_id: item.agent_id,
          label: item.label,
          welcome_title: item.welcome_title?.trim() || null,
          quick_questions: (item.quick_questions || []).map((q) => q.trim()).filter(Boolean).slice(0, 6),
          use_agent_avatar: item.use_agent_avatar !== false,
        })),
      })
      if (Array.isArray(landingSaved?.items)) {
        setLandingDemoAgents(
          landingSaved.items.map((row: LandingDemoAgent) => ({
            ...row,
            quick_questions: row.quick_questions ?? [],
            use_agent_avatar: row.use_agent_avatar !== false,
          }))
        )
      }
      setStatus('Configuration des widgets mise à jour ✅')
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : err?.message || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }, [config, landingDemoAgents])

  if (loading) {
    return (
      <ConfigPageLayout title="WIDGETS" description="Gérez l'affichage des widgets de chat sur la plateforme">
        <div className="flex items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      </ConfigPageLayout>
    )
  }

  return (
    <ConfigPageLayout title="WIDGETS" description="Gérez l'affichage des widgets de chat sur la plateforme. Ces paramètres s'appliquent globalement.">
      <div className="grid gap-6 lg:grid-cols-2">
          {/* Widget Public */}
          <section className="space-y-4 rounded-3xl border border-zinc-200/70 bg-white/95 p-6 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 dark:bg-blue-900/30">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Widget Public</h2>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                  Widget affiché sur les pages publiques (landing page, tarifs, etc.) pour les visiteurs non connectés.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-xl border border-blue-200/70 bg-blue-50/50 p-4 dark:border-blue-900/50 dark:bg-blue-900/20">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-zinc-900 dark:text-white">Activer le widget public</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Désactivez temporairement le widget sur toutes les pages publiques
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('public_widget_enabled')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 ${
                  config.public_widget_enabled
                    ? 'bg-blue-500'
                    : 'bg-zinc-300 dark:bg-zinc-700'
                }`}
                disabled={saving}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                    config.public_widget_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="rounded-xl border border-zinc-200/70 bg-zinc-50/50 p-4 dark:border-zinc-800 dark:bg-zinc-800/30">
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                <strong>Note:</strong> Ce widget est destiné aux visiteurs non authentifiés. Il utilise l'agent public configuré dans la section "Agent Public" de la configuration globale.
              </p>
            </div>

            <div className="space-y-4 rounded-xl border border-violet-200/70 bg-violet-50/50 p-4 dark:border-violet-900/60 dark:bg-violet-900/20">
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Agents du simulateur de la landing</h3>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                  Configurez ici les boutons de la section “Tester un agent en action” de la landing publique.
                  Si aucun agent n’est configuré, la section est masquée automatiquement.
                </p>
              </div>

              {landingDemoAgents.map((item, index) => (
                <div key={`${item.agent_id}-${index}`} className="space-y-3 rounded-lg border border-zinc-200/70 bg-white/80 p-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                  <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto] md:items-start">
                    <select
                      value={item.agent_id}
                      onChange={(e) => {
                        const nextAgentId = Number(e.target.value)
                        const nextAgent = agents.find((a) => a.id === nextAgentId)
                        setLandingDemoAgents((prev) => prev.map((row, rowIdx) => (
                          rowIdx === index
                            ? {
                                ...row,
                                agent_id: nextAgentId,
                                agent_name: nextAgent?.name || row.agent_name,
                                subtitle: nextAgent?.subtitle || null,
                                avatar_url: null,
                              }
                            : row
                        )))
                      }}
                      className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
                    >
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => {
                        const nextLabel = e.target.value
                        setLandingDemoAgents((prev) => prev.map((row, rowIdx) => (
                          rowIdx === index ? { ...row, label: nextLabel } : row
                        )))
                      }}
                      placeholder="Label du bouton"
                      className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setLandingDemoAgents((prev) => prev.filter((_, rowIdx) => rowIdx !== index))}
                      className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/30"
                    >
                      Supprimer
                    </button>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">Phrase d&apos;accueil (écran vide)</label>
                    <textarea
                      value={item.welcome_title ?? ''}
                      onChange={(e) => {
                        const v = e.target.value
                        setLandingDemoAgents((prev) => prev.map((row, rowIdx) => (
                          rowIdx === index ? { ...row, welcome_title: v || null } : row
                        )))
                      }}
                      rows={2}
                      placeholder="Ex. Bonjour, comment puis-je vous aider ?"
                      className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">Questions suggérées (max 6)</label>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {Array.from({ length: 6 }).map((_, qIdx) => {
                        const slots = [...(item.quick_questions || [])]
                        while (slots.length < 6) slots.push('')
                        const q = slots[qIdx] ?? ''
                        return (
                          <input
                            key={qIdx}
                            type="text"
                            value={q}
                            onChange={(e) => {
                              const next = e.target.value
                              setLandingDemoAgents((prev) => prev.map((row, rowIdx) => {
                                if (rowIdx !== index) return row
                                const s = [...(row.quick_questions || [])]
                                while (s.length < 6) s.push('')
                                s[qIdx] = next
                                return { ...row, quick_questions: s }
                              }))
                            }}
                            placeholder={`Question ${qIdx + 1}`}
                            className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
                          />
                        )
                      })}
                    </div>
                  </div>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-800 dark:text-zinc-200">
                    <input
                      type="checkbox"
                      checked={item.use_agent_avatar !== false}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setLandingDemoAgents((prev) => prev.map((row, rowIdx) => (
                          rowIdx === index ? { ...row, use_agent_avatar: checked } : row
                        )))
                      }}
                      className="rounded border-zinc-300 text-violet-600 focus:ring-violet-500 dark:border-zinc-600"
                    />
                    Afficher l&apos;avatar de l&apos;agent dans le simulateur
                  </label>
                </div>
              ))}

              <button
                type="button"
                disabled={agentsLoading || agents.length === 0}
                onClick={() => {
                  const fallback = agents[0]
                  if (!fallback) return
                  setLandingDemoAgents((prev) => [
                    ...prev,
                    {
                      agent_id: fallback.id,
                      label: fallback.name,
                      agent_name: fallback.name,
                      subtitle: fallback.subtitle || null,
                      welcome_title: null,
                      quick_questions: [],
                      use_agent_avatar: true,
                      avatar_url: null,
                    },
                  ])
                }}
                className="w-full rounded-lg border border-dashed border-violet-300 bg-white/90 px-4 py-2 text-sm text-violet-700 hover:bg-violet-100 disabled:opacity-50 dark:border-violet-900 dark:bg-zinc-900 dark:text-violet-300 dark:hover:bg-zinc-800"
              >
                + Ajouter un agent au simulateur
              </button>
            </div>
          </section>

          {/* Widget Interne */}
          <section className="space-y-4 rounded-3xl border border-zinc-200/70 bg-white/95 p-6 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 dark:bg-emerald-900/30">
                <svg className="h-6 w-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Widget Interne</h2>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                  Widget affiché dans l'application pour les utilisateurs connectés (tableau de bord, etc.).
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-xl border border-emerald-200/70 bg-emerald-50/50 p-4 dark:border-emerald-900/50 dark:bg-emerald-900/20">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-zinc-900 dark:text-white">Activer le widget interne</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Désactivez temporairement le widget pour les utilisateurs connectés
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('internal_widget_enabled')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 ${
                  config.internal_widget_enabled
                    ? 'bg-emerald-500'
                    : 'bg-zinc-300 dark:bg-zinc-700'
                }`}
                disabled={saving}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                    config.internal_widget_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Agent Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-zinc-900 dark:text-white">
                Agent Support Totemium
              </label>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">
                Sélectionnez l'agent qui répondra aux questions des utilisateurs sur la plateforme.
              </p>
              <select
                value={config.internal_agent_id ?? ''}
                onChange={(e) => {
                  const value = e.target.value ? Number(e.target.value) : null
                  setConfig((prev) => ({ ...prev, internal_agent_id: value }))
                  setStatus(null)
                  setError(null)
                }}
                disabled={saving || agentsLoading}
                className="w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
              >
                <option value="">-- Aucun agent sélectionné --</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} {agent.subtitle ? `(${agent.subtitle})` : ''}
                  </option>
                ))}
              </select>
              {agentsLoading && (
                <p className="text-xs text-zinc-400">Chargement des agents...</p>
              )}
            </div>

            {/* ✅ NOUVEAU : Checkbox pour rendre le widget interne accessible à tous */}
            {config.internal_agent_id && (
              <div className="space-y-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 p-4 dark:border-emerald-900/60 dark:bg-emerald-900/20">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="allow_internal_agent_for_all_users"
                    className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500/20 dark:border-emerald-700"
                    checked={config.allow_internal_agent_for_all_users}
                    onChange={(e) => {
                      setConfig((prev) => ({ ...prev, allow_internal_agent_for_all_users: e.target.checked }))
                      setStatus(null)
                      setError(null)
                    }}
                    disabled={saving}
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor="allow_internal_agent_for_all_users" 
                      className="block text-sm font-semibold text-zinc-900 cursor-pointer dark:text-white"
                    >
                      Rendre le widget interne accessible à tous les utilisateurs connectés
                    </label>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                      Si activé, tous les utilisateurs authentifiés pourront utiliser le widget interne, 
                      même s'ils ne sont pas propriétaires de l'agent assigné. 
                      Désactivez cette option pour restreindre l'accès au propriétaire uniquement.
                    </p>
                  </div>
                </div>
                
                {/* Indicateur visuel de l'état */}
                <div className={`mt-3 p-3 rounded-lg text-sm ${
                  config.allow_internal_agent_for_all_users 
                    ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900' 
                    : 'bg-orange-100 text-orange-800 border border-orange-200 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-900'
                }`}>
                  <strong>État actuel :</strong> Le widget interne est{' '}
                  {config.allow_internal_agent_for_all_users 
                    ? 'accessible à tous les utilisateurs connectés ✅' 
                    : 'accessible uniquement au propriétaire de l\'agent ⚠️'}
                </div>
              </div>
            )}

            {/* Suggestions Management */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-zinc-900 dark:text-white">
                Questions Rapides
              </label>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">
                Configurez les boutons de suggestions qui apparaîtront dans le widget.
              </p>
              
              {config.internal_widget_suggestions.map((suggestion, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Label du bouton"
                    value={suggestion.label}
                    onChange={(e) => {
                      const newSuggestions = [...config.internal_widget_suggestions]
                      newSuggestions[index].label = e.target.value
                      setConfig((prev) => ({ ...prev, internal_widget_suggestions: newSuggestions }))
                    }}
                    className="flex-1 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
                  />
                  <input
                    type="text"
                    placeholder="Question complète"
                    value={suggestion.question}
                    onChange={(e) => {
                      const newSuggestions = [...config.internal_widget_suggestions]
                      newSuggestions[index].question = e.target.value
                      setConfig((prev) => ({ ...prev, internal_widget_suggestions: newSuggestions }))
                    }}
                    className="flex-[2] rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newSuggestions = config.internal_widget_suggestions.filter((_, i) => i !== index)
                      setConfig((prev) => ({ ...prev, internal_widget_suggestions: newSuggestions }))
                    }}
                    className="px-3 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/30"
                  >
                    ✕
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  setConfig((prev) => ({
                    ...prev,
                    internal_widget_suggestions: [...prev.internal_widget_suggestions, { label: '', question: '' }]
                  }))
                }}
                className="w-full rounded-lg border border-dashed border-zinc-300 bg-zinc-50 px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-400 dark:hover:bg-zinc-800"
              >
                + Ajouter une suggestion
              </button>
            </div>

            <div className="rounded-xl border border-zinc-200/70 bg-zinc-50/50 p-4 dark:border-zinc-800 dark:bg-zinc-800/30">
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                <strong>Note:</strong> Ce widget est affiché dans le header de la plateforme. Il permet aux utilisateurs d'obtenir de l'aide sur Totemium. L'agent sélectionné doit être configuré avec une base de connaissances sur la plateforme.
              </p>
            </div>
          </section>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-full border border-emerald-200/70 bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </button>
        </div>

        {status && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
            {status}
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

      {/* Future Options Placeholder */}
      <section className="mt-4 space-y-4 rounded-3xl border border-dashed border-zinc-300 bg-zinc-50/50 p-6 dark:border-zinc-700 dark:bg-zinc-900/50">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Options futures</h3>
        </div>
        <p className="text-xs text-zinc-400 dark:text-zinc-500">
          D'autres options de personnalisation du widget seront disponibles ici prochainement : personnalisation des couleurs, messages d'accueil, comportement, etc.
        </p>
      </section>
    </ConfigPageLayout>
  )
}
